import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ConfirmationIcon } from '@icons';

import {
  AmountInput,
  Button,
  ConfirmationModal,
  Dropdown,
  Input,
  Modal,
  RadioButton
} from '@components';
import CalendarInput from '@components/calendar-input/CalendarInput';
import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import { TimeZones } from '@constants/date';
import { ErrorCode } from '@constants/errorCodes';
import { FEATURE_FLAGS } from '@constants/feature';
import {
  AccountType,
  OrgStatus,
  PER_SPLIT_AMOUNT_MIN,
  SubscriptionPlanExpiredMessage,
  SubscriptionStatus
} from '@constants/generic';
import UserPermissionTypes from '@constants/permissions';
import { GROUPS, SUBSCRIPTIONS } from '@constants/routes';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import {
  branchInitialState,
  NewBankType
} from '@features/branch-management/constants';
import { getUniqueAccountsDataForFeeCategory } from '@features/branch-management/helper';
import { useLazyGetOrgAccountDetailsQuery } from '@features/branch-management/queries';
import {
  AmountMode,
  amountModeOptions,
  AmountType,
  amountTypeOptions
} from '@features/transactions/constants';
import { getModalTitleAndSubText } from '@features/transactions/helper';
import { useMarkAsPaidMutation } from '@features/transactions/queires/TransactionsQuery';
import useNotify from '@hooks/notify';
import { ApiError, MarkAsPaidModalProps, ValidationError } from '@types';
import { formatISODate, getFormattedDateTimeWithOffset } from '@utils/date';
import { buildQueryString } from '@utils/queryStringBuilder';
import { BankAccount } from 'types/account-detail';
import { DropdownOption } from 'types/dropdown';

import PaymentSuccess from '../payment-success/PaymentSuccess';

const MarkAsPaidModal = ({
  isOpen,
  handleCancel,
  transaction,
  onSuccessUpdate,
  branchId,
  organizationId,
  flow,
  handleViewDetails,
  onSplitPaymentOrderIdChange,
  onCustomMarkAsPaid
}: MarkAsPaidModalProps) => {
  const [amountType, setAmountType] = useState<AmountType>(AmountType.FULL);
  const [amount, setAmount] = useState<string>('0');
  const [amountMode, setAmountMode] = useState<AmountMode>(AmountMode.CASH);
  const [paymentDate, setPaymentDate] = useState<string>(
    getFormattedDateTimeWithOffset(new Date(), TimeZones['Asia/Kolkata'])
  );

  const [note, setNote] = useState<string>('');
  const [validationError, setValidationError] = useState<ValidationError>({});
  const [paidAmount, setPaidAmount] = useState<number | null>(null);
  const [preferredAccount, setPreferredAccount] =
    useState<BankAccount>(branchInitialState);
  const [isConformationModalOpen, setIsConformationModalOpen] =
    useState<boolean>(false);

  const [markAsPaid, { isLoading: isLoadingMarkAsPaid }] =
    useMarkAsPaidMutation();
  const { createErrorAlert, createSuccessAlert } = useNotify();
  const { orgConfig } = useSelector((state: any) => state.user);
  const { rolesAndPermissions } = useSelector((state: any) => state.user);
  const { currentSubscription } = useSelector((state: any) => state.user);
  const { orgFeatureFlags } = useSelector((state: any) => state.user);

  const navigate = useNavigate();
  const { currentOrg } = useSelector((state: any) => state.user);

  const minAmount = orgConfig?.group_member_amount_min || PER_SPLIT_AMOUNT_MIN;
  const pendingAmount = Number(
    (Number(transaction.amount) - Number(transaction.paid_amount)).toFixed(2)
  );

  const shouldValidatePartialSplit =
    transaction.is_split_payment &&
    pendingAmount > minAmount &&
    amountType === AmountType.PARTIAL;

  const getPartialSplitAmountErrors = useCallback(
    (value: string): ValidationError => {
      if (!shouldValidatePartialSplit) return {};
      const n = Number(value);
      if (Number.isNaN(n)) return {};
      if (n < minAmount) {
        return {
          minAmount: `Amount should be minimum Rs ${minAmount} or more`
        };
      }
      if (n > pendingAmount) {
        return {
          maxAmount: `Amount should be less than Rs ${pendingAmount}`
        };
      }
      return {};
    },
    [shouldValidatePartialSplit, minAmount, pendingAmount]
  );

  const getAmountToBePaid = () => {
    if (amountType === AmountType.FULL) {
      return pendingAmount;
    }
    return amount;
  };

  const [getPrimaryAccountDetails, { data: primaryAccountDetails }] =
    useLazyGetOrgAccountDetailsQuery();

  const isOrgLite =
    (currentOrg?.status === OrgStatus.KYC_PENDING ||
      currentOrg?.status === OrgStatus.KYC_SUBMITTED) &&
    currentOrg?.type === AccountType.ORGANIZATION &&
    !currentOrg?.is_kyc_deferred;

  const isIndividualLite =
    (currentOrg?.status === OrgStatus.KYC_PENDING ||
      currentOrg?.status === OrgStatus.KYC_SUBMITTED) &&
    currentOrg?.type === AccountType.INDIVIDUAL &&
    !currentOrg?.is_kyc_deferred;

  const isLite = isOrgLite || isIndividualLite;

  const showBankAccountDropdown =
    amountMode !== AmountMode.CASH &&
    orgFeatureFlags?.includes(
      FEATURE_FLAGS.MANUAL_PAYMENT_BANK_ACCOUNT_MAPPING
    ) &&
    !isLite;

  const handleMarkAsPaid = async () => {
    if (currentSubscription?.status === SubscriptionStatus.EXPIRED) {
      createErrorAlert(SubscriptionPlanExpiredMessage);
      if (
        rolesAndPermissions.permissions.includes(
          UserPermissionTypes.SUBSCRIPTION_VIEW
        )
      ) {
        navigate(SUBSCRIPTIONS.PLAN_DETAILS);
      }
    } else {
      const markAsPaidBody = {
        payment_order_id: transaction.id,
        amount: getAmountToBePaid(),
        payment_method: amountMode,
        payment_description: note,
        paid_date: paymentDate,
        ...(showBankAccountDropdown && {
          settlement_account_id: preferredAccount?.accountDetailId
        })
      };

      const resp = onCustomMarkAsPaid
        ? await onCustomMarkAsPaid(markAsPaidBody)
        : await markAsPaid({
            OrgAndBranchId: { organizationId, branchId },
            body: markAsPaidBody
          });

      if ('data' in resp) {
        createSuccessAlert('Payment marked as paid successfully');
        onSuccessUpdate();
        setPaidAmount(Number(getAmountToBePaid()));

        onSplitPaymentOrderIdChange?.({
          splitPaymentOrderId: resp.data?.split_payment_order_id,
          paymentOrderId: resp.data?.payment_order_id
        });
      } else if ('error' in resp) {
        const error = resp.error as ApiError;
        createErrorAlert(error.data?.message);
        if (error?.data?.errorCode === ErrorCode.ERR_SUBSCRIPTION_EXPIRED) {
          if (
            rolesAndPermissions.permissions.includes(
              UserPermissionTypes.SUBSCRIPTION_VIEW
            )
          ) {
            navigate(SUBSCRIPTIONS.PLAN_DETAILS);
          } else if (flow === 'groupLevel') {
            navigate(GROUPS.HOME);
          }
        }
      }
      setIsConformationModalOpen(false);
    }
  };

  const handleConfirm = async () => {
    sendMixpanelEvent(Event.TRANSACTION_MARK_AS_PAID_MODAL_CONFIRM_CLICKED, {
      paymentMethod: amountMode,
      paymentDescription: note,
      paid_date: formatISODate({
        date: paymentDate,
        format: 'DD MMM YYYY'
      }),
      recorded_on: formatISODate({
        date: new Date().toISOString(),
        format: 'DD MMM YYYY at hh:mm am/pm'
      })
    });
    if (shouldValidatePartialSplit) {
      const partialErrors = getPartialSplitAmountErrors(amount);
      if (partialErrors.minAmount || partialErrors.maxAmount) {
        setValidationError((prev) => ({
          ...prev,
          ...partialErrors
        }));
        setIsConformationModalOpen(false);
        return;
      }
    }
    handleMarkAsPaid();
  };

  const hasDecimal = pendingAmount % 1 !== 0;

  // Convert amountModeOptions to dropdown format
  const paymentMethodOptions = amountModeOptions.map((option, index) => ({
    id: index + 1,
    label: option.label,
    value: option.value
  }));

  const handlePaymentMethodChange = (selectedValue: string) => {
    setAmountMode(selectedValue as AmountMode);
  };

  const handlePaymentDateChange = (date?: Date) => {
    if (date) {
      setPaymentDate(date.toISOString());
    }
  };

  const handleGetPrimaryAccountDetails = async (id: string) => {
    await getPrimaryAccountDetails({
      organizationId,
      queryString: buildQueryString({
        branch_id: id || branchId
      })
    });
  };

  const availableAccouts = getUniqueAccountsDataForFeeCategory(
    primaryAccountDetails || {}
  );

  const dropdownOptions: DropdownOption[] = availableAccouts?.map(
    (account: BankAccount | NewBankType) => ({
      id: String(account.accountDetailId),
      value: String(account.accountDetailId),
      element: (
        <div className="flex items-center gap-2">
          <img
            src={account.bank_logo}
            alt="Bank Logo"
            className="size-9 rounded-lg border border-grey10 object-contain"
          />
          <div className="flex flex-col items-start">
            <div className="truncate !text-sm font-medium leading-tight text-content">
              {account.bank_account_name}
            </div>
            <div className="truncate !text-xs leading-tight text-grey">
              {account.bank_account_number}
            </div>
          </div>
        </div>
      )
    })
  );

  const handleConfirmModal = () => {
    if (shouldValidatePartialSplit) {
      const partialErrors = getPartialSplitAmountErrors(amount);
      if (partialErrors.minAmount || partialErrors.maxAmount) {
        setValidationError((prev) => ({
          ...prev,
          ...partialErrors
        }));
        return;
      }
    }
    setValidationError((prev) => {
      const next = { ...prev };
      delete next.minAmount;
      delete next.maxAmount;
      return next;
    });
    setIsConformationModalOpen(true);
  };

  const handleCancelModal = () => {
    setIsConformationModalOpen(false);
  };

  const handleChange = (accountId: string) => {
    const selectedAccount = availableAccouts.find(
      (account) => String(account.accountDetailId) === accountId
    );

    if (selectedAccount) {
      setPreferredAccount(selectedAccount);
    }
  };

  const renderSplitPaymentDetails = () => (
    <div className="flex flex-col gap-4">
      <div className="text-sm font-medium text-grey">
        Amount Paid <span className="text-error">*</span>
      </div>
      <div className="rounded-xl border">
        {amountTypeOptions.map((option, index) => (
          <div
            key={`amount_type_${option.value}`}
            className={`flex flex-col gap-2 p-3 ${
              validationError ? 'mb-2' : ''
            } ${index > 0 ? 'border-t' : ''}`}
          >
            <RadioButton
              isSelected={amountType === option.value}
              onChange={(e) => {
                const nextType = e.target.value as AmountType;
                setAmountType(nextType);
                if (nextType === AmountType.FULL) {
                  setValidationError((prev) => {
                    const next = { ...prev };
                    delete next.minAmount;
                    delete next.maxAmount;
                    return next;
                  });
                }
              }}
              label={option.label}
              value={option.value}
              key={`amount_type_${option.value}`}
              labelStyle="!text-content !text-base"
            />
            {amountType === AmountType.PARTIAL &&
              option.value === AmountType.PARTIAL && (
                <div className="ml-8">
                  <AmountInput
                    value={amount}
                    placeholder="Enter Amount"
                    onAmountChange={(value) => {
                      setAmount(value);
                      setValidationError((prev) => {
                        const partialErrors =
                          getPartialSplitAmountErrors(value);
                        const next = { ...prev };
                        if (partialErrors.minAmount)
                          next.minAmount = partialErrors.minAmount;
                        else delete next.minAmount;
                        if (partialErrors.maxAmount)
                          next.maxAmount = partialErrors.maxAmount;
                        else delete next.maxAmount;
                        return next;
                      });
                    }}
                    error={
                      !!validationError.minAmount || !!validationError.maxAmount
                    }
                    errorMessage={
                      validationError.minAmount
                        ? validationError.minAmount
                        : validationError.maxAmount
                    }
                    allowDecimals={!!hasDecimal}
                    className="!bg-white"
                  />
                </div>
              )}
          </div>
        ))}
      </div>
    </div>
  );

  useEffect(() => {
    if (branchId) {
      handleGetPrimaryAccountDetails(branchId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  useEffect(() => {
    if (!transaction.is_split_payment || pendingAmount <= minAmount) {
      setAmount(String(pendingAmount));
      setAmountType(AmountType.FULL);
    } else {
      setAmount('');
      setAmountType(AmountType.PARTIAL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction.is_split_payment, pendingAmount, minAmount]);

  return (
    <>
      {!isConformationModalOpen && (
        <Modal isOpen={isOpen} onCancel={handleCancel} isCloseIconRequired>
          <div className="flex w-118 flex-col gap-4 rounded-lg bg-white p-6">
            {paidAmount ? (
              <PaymentSuccess
                amount={paidAmount}
                handleCancel={handleCancel}
                handleViewDetails={handleViewDetails}
              />
            ) : (
              <>
                <div className="flex flex-col gap-2">
                  <div className="text-xl font-medium text-content">
                    {
                      getModalTitleAndSubText(
                        minAmount,
                        pendingAmount,
                        transaction
                      )?.title
                    }
                  </div>
                  <div className="text-sm text-grey">
                    Enter payment details to mark this transaction as paid.
                  </div>
                </div>

                {transaction.is_split_payment &&
                  pendingAmount > minAmount &&
                  renderSplitPaymentDetails()}

                <div className="flex flex-col gap-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-grey">
                      Payment Mode <span className="text-error">*</span>
                    </h3>
                    <Dropdown
                      currentFilter={amountMode}
                      filterOptions={paymentMethodOptions}
                      onSelectFilter={handlePaymentMethodChange}
                      placeholder="Select payment method"
                      className="w-63"
                      buttonClassName="!w-full !min-w-full !bg-white !border-greyStroke !rounded-xl !h-14"
                      placeholderStyle="!text-grey !font-normal"
                      listId="payment-method"
                      optionsStyle="!w-full !left-0"
                    />
                  </div>
                </div>
                {showBankAccountDropdown && (
                  <div className="flex flex-col gap-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-grey">
                        Bank Account
                      </h3>
                      <Dropdown
                        filterOptions={dropdownOptions}
                        currentFilter={
                          preferredAccount?.accountDetailId
                            ? String(preferredAccount?.accountDetailId)
                            : ''
                        }
                        onSelectFilter={(accountId: string) => {
                          handleChange(accountId);
                        }}
                        placeholder="Select Bank"
                        className="w-63"
                        buttonClassName="!w-full !min-w-full !bg-white !border-greyStroke !rounded-xl !h-14"
                        placeholderStyle="!text-grey !font-normal"
                        listId="bank-account"
                        optionsStyle="!w-74 !right-0 max-h-83 overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-track]:bg-gray-100"
                      />
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-grey">
                      Payment Date <span className="text-error">*</span>
                    </h3>
                    <CalendarInput
                      selectedDate={new Date(paymentDate)}
                      handleDateChange={handlePaymentDateChange}
                      className="!w-63 !bg-white"
                      labelStyle="hidden"
                      inputStyle="!bg-white !text-content !border-greyStroke"
                      isMaxDateRequired
                      maxDate={new Date()}
                      label="hidden"
                      modalTitle=""
                      modalTitleStyle="!mt-0"
                    />
                  </div>
                </div>

                <Input
                  placeholder="Notes"
                  value={note}
                  handleInputChange={(value) => setNote(value)}
                  maxLength={100}
                  inputStyle="!bg-grey10 !text-content"
                />

                <Button
                  label="Confirm"
                  handleClick={handleConfirmModal}
                  disabled={
                    transaction.is_split_payment &&
                    pendingAmount > minAmount &&
                    amountType === AmountType.PARTIAL &&
                    (amount === '' || Number(amount) === 0)
                  }
                />
              </>
            )}
          </div>
        </Modal>
      )}
      {isConformationModalOpen && (
        <ConfirmationModal
          isOpen={isConformationModalOpen}
          className="w-118 !bg-white !pb-6"
          title="Confirmation"
          titleStyle="!font-semibold !mb-2"
          message={
            <div>
              Are you sure you want to mark this payment of{' '}
              <span className="inline-flex items-baseline">
                <CurrencyAmount
                  value={getAmountToBePaid()}
                  className="gap-0.5 font-semibold"
                  symbolClass="!text-[0.975rem]"
                />
              </span>{' '}
              from <span className="font-semibold">{transaction.name}</span> as
              paid?
            </div>
          }
          messageStyle="!text-base !mb-8 !max-w-[400px] !mx-auto"
          handleConfirm={handleConfirm}
          handleCancel={handleCancelModal}
          primaryButtonVariant="primary"
          secondaryButtonVariant="tertiary"
          primaryButtonSize="medium"
          confirmButtonStyle="!py-2 !text-base !font-medium"
          secondaryButtonSize="medium"
          cancelButtonStyle="bg-transparent !text-base !font-medium"
          isConfirmButtonLoading={isLoadingMarkAsPaid}
          ModalIcon={ConfirmationIcon}
          customModalIconStyle="!w-12 !h-12 !min-w-12 !min-h-12"
          isClickOutsideRequired={false}
          confirmButtonLabelStyle="!text-base !font-medium"
          cancelButtonLabelStyle="!text-base !font-medium"
        />
      )}
    </>
  );
};

export default MarkAsPaidModal;
